import * as RadixTooltip from '@radix-ui/react-tooltip';
import cx from 'classnames';
import {
  Fragment,
  isValidElement,
  type ReactNode,
  type Ref,
  useEffect,
  useRef,
  useState,
} from 'react';
import styles from './Tooltip.module.css';

// ─── Types ─────────────────────────────────────────────────────────────────

type TooltipPlacement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end';

export type TooltipProps = {
  /** Optional test identifier — applied to the tooltip content element */
  dataTestId?: string;
  /** The element that triggers the tooltip. Text or several nodes are wrapped in a <span> */
  children: ReactNode;
  /** Tooltip body content. When empty (undefined, null, false or ''), only the trigger is rendered */
  content: ReactNode;
  /** Preferred placement of the tooltip relative to the trigger */
  placement?: TooltipPlacement;
  /** Delay in seconds before the tooltip appears on hover */
  delay?: number;
  /** Controlled open state */
  isOpen?: boolean;
  /** Called when the open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Additional CSS class on the tooltip content panel */
  className?: string;
  /** Ref forwarded to the tooltip content element */
  ref?: Ref<HTMLDivElement>;
};

export type { TooltipPlacement };

// ─── Helpers ───────────────────────────────────────────────────────────────

// Radix uses 'top' | 'bottom' | 'left' | 'right' as side,
// and 'start' | 'end' | 'center' as align.
function parsePlacement(placement: TooltipPlacement): {
  side: 'top' | 'bottom' | 'left' | 'right';
  align: 'start' | 'center' | 'end';
} {
  const [side, align] = placement.split('-') as [
    'top' | 'bottom' | 'left' | 'right',
    'start' | 'end' | undefined,
  ];
  return { side, align: align ?? 'center' };
}

function isEmptyContent(content: ReactNode): boolean {
  return content === undefined || content === null || content === false || content === '';
}

// ─── Component ─────────────────────────────────────────────────────────────

export function Tooltip(props: TooltipProps) {
  const {
    dataTestId,
    children,
    content,
    placement = 'top',
    delay = 0,
    isOpen,
    onOpenChange,
    className,
    ref,
  } = props;

  // Keep the same tree whether or not there is something to show, so toggling `content`
  // never remounts the trigger.
  const hasContent = !isEmptyContent(content);
  const { side, align } = parsePlacement(placement);
  // Radix delayDuration is in ms
  const delayDuration = delay * 1000;

  // Radix closes the tooltip on pointer down and on click. Users click info icons to read
  // them, so a press on the trigger must not close it: closing stays on pointer leave, blur
  // and Escape.
  const [internalOpen, setInternalOpen] = useState(false);
  const open = hasContent ? (isOpen ?? internalOpen) : false;
  const openRef = useRef(open);
  openRef.current = open;
  const isPressingRef = useRef(false);
  const pressTimerRef = useRef<number>(undefined);

  useEffect(() => () => window.clearTimeout(pressTimerRef.current), []);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isPressingRef.current) return;
    if (nextOpen && !hasContent) return;
    setInternalOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const markPressed = () => {
    // A press before the delay elapsed makes Radix drop the pending open: show it right away.
    if (!openRef.current) handleOpenChange(true);
    isPressingRef.current = true;
    window.clearTimeout(pressTimerRef.current);
    // Radix's own handlers run in the same event dispatch, before this timer fires.
    pressTimerRef.current = window.setTimeout(() => {
      isPressingRef.current = false;
    }, 0);
  };

  return (
    <RadixTooltip.Provider delayDuration={delayDuration}>
      <RadixTooltip.Root open={open} onOpenChange={handleOpenChange}>
        <RadixTooltip.Trigger
          asChild
          onPointerDownCapture={markPressed}
          onClickCapture={markPressed}
        >
          {isValidElement(children) && children.type !== Fragment ? (
            children
          ) : (
            <span>{children}</span>
          )}
        </RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            ref={ref}
            side={side}
            align={align}
            sideOffset={6}
            className={cx(styles.content, className)}
            {...(dataTestId !== undefined && { 'data-testid': dataTestId })}
          >
            {content}
            <RadixTooltip.Arrow className={styles.arrow} />
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}
